import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, RefreshCw, UserCheck, Loader2, PauseCircle, X } from 'lucide-react'
import { usersApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

const getUserId   = (u) => u?._id || u?.id
const getUserName = (u) => u?.name || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || '—'

const ROLE_BADGE = {
  student: 'bg-green-50 text-green-700',
  teacher: 'bg-cyan-50 text-cyan-700',
  parent:  'bg-amber-50 text-amber-700',
  admin:   'bg-violet-50 text-violet-700',
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(5)].map((_, i) => (
        <td key={i} className="table-td"><div className="h-3 bg-gray-100 rounded w-3/4" /></td>
      ))}
    </tr>
  )
}

export default function InactiveUsersPage() {
  const navigate = useNavigate()
  const [users, setUsers]               = useState([])
  const [loading, setLoading]           = useState(true)
  const [search, setSearch]             = useState('')
  const [reactivateTarget, setReactivateTarget] = useState(null)
  const [reactivating, setReactivating] = useState(false)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const data = await usersApi.getAll({ accountStatus: 'all', limit: 2000 })
      const all = Array.isArray(data) ? data : data?.users ?? []
      setUsers(all.filter((u) => u.accountStatus === 'inactive'))
    } catch {
      toast.error('Failed to load inactive users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter((u) =>
      getUserName(u).toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.mobile || '').includes(search.trim()) ||
      (u.admissionNo || '').toLowerCase().includes(q)
    )
  }, [search, users])

  const handleReactivate = async () => {
    if (!reactivateTarget) return
    try {
      setReactivating(true)
      await usersApi.updateAccountStatus(getUserId(reactivateTarget), 'active')
      setUsers((prev) => prev.filter((u) => getUserId(u) !== getUserId(reactivateTarget)))
      toast.success(`${getUserName(reactivateTarget)} reactivated`)
      setReactivateTarget(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reactivate user')
    } finally {
      setReactivating(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Inactive Users</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? '...' : `${users.length} suspended account${users.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={fetchUsers} className="btn-secondary p-2 self-start sm:self-auto" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
        <PauseCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-red-700">
          Inactive users are suspended and cannot log in. Their data is fully preserved.
          Click <span className="font-semibold">Reactivate</span> to restore access.
        </p>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, mobile or admission no..."
            className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {!loading && (
          <p className="text-xs text-gray-400 mt-2">
            <span className="font-semibold text-gray-700">{filtered.length}</span> inactive user{filtered.length !== 1 ? 's' : ''}
            {users.length !== filtered.length ? ` of ${users.length}` : ''}
          </p>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-th">User</th>
                <th className="table-th">Role</th>
                <th className="table-th hidden md:table-cell">Mobile</th>
                <th className="table-th hidden lg:table-cell">Deactivated On</th>
                <th className="table-th">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-16 text-gray-400">
                    <PauseCircle className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                    <p className="font-medium">{search ? 'No matches found' : 'No inactive users'}</p>
                    <p className="text-xs mt-1">{search ? 'Try a different search term' : 'All users are currently active'}</p>
                  </td>
                </tr>
              ) : filtered.map((u) => {
                const uid  = getUserId(u)
                const name = getUserName(u)
                const badge = ROLE_BADGE[u.role] || 'bg-gray-100 text-gray-600'
                const deactivatedAt = u.deactivatedAt
                  ? new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(u.deactivatedAt))
                  : '—'
                return (
                  <tr key={uid} className="hover:bg-gray-50 transition-colors opacity-75">
                    <td className="table-td">
                      <div className="flex items-center gap-2.5">
                        <Avatar src={u.avatar} name={name} size="sm" />
                        <div>
                          <button
                            onClick={() => navigate(`/users/${uid}`)}
                            className="font-medium text-gray-700 hover:text-primary-700 transition-colors text-left"
                          >
                            {name}
                          </button>
                          <p className="text-xs text-gray-400">{u.email || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-td">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge}`}>
                        {u.role || '—'}
                      </span>
                    </td>
                    <td className="table-td hidden md:table-cell text-gray-500">{u.mobile || '—'}</td>
                    <td className="table-td hidden lg:table-cell text-gray-500">{deactivatedAt}</td>
                    <td className="table-td">
                      <button
                        onClick={() => setReactivateTarget(u)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-green-700 border border-green-200 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <UserCheck className="w-3.5 h-3.5" /> Reactivate
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reactivate confirm modal */}
      {reactivateTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Reactivate User?</h3>
            <p className="text-sm text-gray-500 mb-5">
              <span className="font-semibold text-gray-700">{getUserName(reactivateTarget)}</span> will be able to log in again.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setReactivateTarget(null)} className="btn-secondary flex-1" disabled={reactivating}>
                Cancel
              </button>
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {reactivating && <Loader2 className="w-4 h-4 animate-spin" />}
                Reactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
